import { useChatGptFeature } from './useChatGptFeature';
import { useEditSmartReply } from './useEditSmartReply';
import { useWriteSmartReply } from './useWriteSmartReply';
import { SmartReplyHook, WriteSmartReplyErrors } from '../smartReplyTypes';
import { useState, useEffect } from 'react';
import { QueryResponseType } from '../../types';
import { CommunicationStylesAnalysis } from '@copilot/common/types/communicationStyles';
import { isEmpty, isNil } from 'lodash';

/**
 * [Smart] Hook for writing or editing a message with Smart Reply
 * @param orgId
 * @param memberId
 * @param contactId
 */
export function useSmartReply(
    orgId: string,
    memberId: string,
    contactId: string,
    communicationStyles?: CommunicationStylesAnalysis
): SmartReplyHook {
    // If ChatGPT is not enabled, return immediately
    const { isChatGptFeatureEnabled, handleChatGptFeatureFlagOff } = useChatGptFeature();
    if (!isChatGptFeatureEnabled) {
        return [
            { loading: false, data: undefined, error: new Error(WriteSmartReplyErrors.disabled) },
            {
                writeSmartReply: handleChatGptFeatureFlagOff,
                regenerateSmartReply: () => {
                    handleChatGptFeatureFlagOff();
                },
                editSmartReply: handleChatGptFeatureFlagOff,
                canUseCommunicationStyles: false,
            },
        ];
    }

    const [writeData, { writeSmartReply, regenerateSmartReply }] = useWriteSmartReply(
        orgId,
        memberId,
        contactId,
        communicationStyles
    );
    const [editData, { editSmartReply }] = useEditSmartReply(communicationStyles);
    const [data, setData] = useState<QueryResponseType<string>>(writeData);

    // TODO COPILOT-6627: improve method of combining data from two queries
    // Currently we are using whether writeData or editData is loading to determine which data to use and showcase to consumer
    useEffect(() => {
        setData(writeData);
    }, [writeData.loading]);

    useEffect(() => {
        setData(editData);
    }, [editData.loading]);

    return [
        data,
        {
            writeSmartReply,
            regenerateSmartReply,
            editSmartReply,
            canUseCommunicationStyles:
                !isNil(communicationStyles) && !isEmpty(communicationStyles.styles),
        },
    ];
}
